import { Link } from 'react-router-dom';
import { MapPin, TrendingUp, DollarSign, ExternalLink, Award } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function UniversityCard({ university }) {
  const formatTuition = (min, max) => {
    return `$${(min / 1000).toFixed(0)}k - $${(max / 1000).toFixed(0)}k`;
  };

  return (
    <article 
      className="bg-white border border-gray-100 hover:border-[hsl(var(--gold))]/50 hover:shadow-xl transition-all duration-300 overflow-hidden h-full flex flex-col" 
      data-testid={`university-card-${university.university_id}`}
    >
      <div className="relative overflow-hidden aspect-[4/3]">
        <img
          src={university.banner_image}
          alt={university.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <div className="flex items-center gap-2">
            <span className="bg-[hsl(var(--gold))] text-primary px-3 py-1 text-xs font-bold flex items-center gap-1">
              <Award size={12} />
              #{university.ranking_global} Global
            </span>
            <span className="bg-white/90 text-primary px-2 py-1 text-xs font-semibold">
              {university.country}
            </span>
          </div>
        </div>
      </div>
      
      <div className="p-6 flex-1 flex flex-col">
        <div className="flex items-start gap-3 mb-4">
          <img 
            src={university.logo} 
            alt={`${university.name} logo`} 
            className="w-14 h-14 rounded-sm object-cover border border-gray-100 bg-white shadow-sm" 
          />
          <div className="flex-1">
            <h3 className="text-xl font-playfair font-bold text-primary mb-1 leading-tight">
              {university.name}
            </h3>
            <p className="text-sm text-gray-500 flex items-center gap-1">
              <MapPin size={14} className="text-secondary" />
              {university.region}, {university.country}
            </p>
          </div>
        </div>

        <p className="text-sm text-gray-600 leading-relaxed mb-5 line-clamp-3 flex-1">
          {university.description}
        </p>

        <div className="grid grid-cols-2 gap-3 mb-5">
          <div className="bg-muted p-3 border-l-2 border-secondary">
            <div className="flex items-center gap-1 text-gray-500 text-xs mb-1">
              <TrendingUp size={12} />
              <span>Acceptance</span>
            </div>
            <div className="text-lg font-bold text-primary">{university.acceptance_rate}%</div>
          </div>
          <div className="bg-muted p-3 border-l-2 border-[hsl(var(--gold))]">
            <div className="flex items-center gap-1 text-gray-500 text-xs mb-1">
              <DollarSign size={12} />
              <span>Tuition/Year</span>
            </div>
            <div className="text-sm font-bold text-primary">{formatTuition(university.tuition_min, university.tuition_max)}</div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-5">
          {university.highlights.slice(0, 3).map((highlight, idx) => (
            <span key={idx} className="bg-muted text-primary px-2 py-1 text-xs font-medium">
              {highlight}
            </span>
          ))}
        </div>

        <div className="flex gap-2 mt-auto pt-4 border-t border-gray-100">
          <Link to={`/universities/${university.university_id}`} className="flex-1">
            <Button 
              className="w-full bg-primary hover:bg-primary/90 text-white font-semibold rounded-sm" 
              data-testid={`view-details-${university.university_id}`}
            >
              View Details
            </Button>
          </Link>
          <Button
            variant="outline"
            size="icon"
            className="rounded-sm border-gray-200 hover:bg-muted hover:border-primary"
            onClick={() => window.open(university.website, '_blank')}
            data-testid={`visit-website-${university.university_id}`}
          >
            <ExternalLink size={18} />
          </Button>
        </div>
      </div>
    </article>
  );
}
